import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import Box from '@quintoandar/cozy-core/Box';
import FeedbackBlock from '@quintoandar/cozy-core/FeedbackBlock';
import Snackbar from '@quintoandar/cozy-core/Snackbar';

import ErrorBoundary from 'components/ErrorBoundary';
import { InstructionHeader } from 'components/InstructionHeader';
import VideoTextInput from 'components/VideoTextInput';
import { logUncaught } from 'utils/log';

import { QueryClient, QueryClientProvider } from 'react-query';
import CloseIcon from '@quintoandar/cozy-icons/Close';
import Skeleton from '@quintoandar/cozy-core/Skeleton';
import Tab from '@quintoandar/cozy-core/Tab';
import Tabs from '@quintoandar/cozy-core/Tabs';
import { makeSelectProfile } from 'containers/Profile/selectors';
import { SHOULD_USE_VIMEO_UPLOAD_FEATURE_FLAG } from 'data/feature-flags/constants';
import { configCatProvider } from 'data/react-query/queries/useFeatureFlag';
import {
  changeBottomButtonPayloadAction,
  changeEnabledButtonAction,
} from '../AnnouncementSteps/actions';
import { VideoUploaderContainer } from './VimeoUploader/VideoUploaderContainer';
import {
  fetchVideoLinkAction,
  saveVideoLinkAction,
  updateHasSnackBarAction,
} from './action';
import {
  makeSelectHasSnackbar,
  makeSelectSnackbarMessage,
  makeSelectSnackbarState,
  makeSelectVideoId,
} from './selectors';
import { useGetAllVideoLinks } from './useGetAllVideoLinks';
import messages from './messages';

export const Video = ({
  fetchVideoLink,
  saveVideoLink,
  updateHasSnackBar,
  hasSnackbar,
  snackbarState,
  snackbarMessage,
  videoId,
  changeEnabledButton,
  params: { house_type: houseType, house_id: houseId },
  additionalProps,
  changeBottomButtonPayload,
  profile,
}) => {
  const queryClientRef = useRef();
  const [isVideoUploadEnabled, setIsVideoUploadEnabled] = useState(false);
  const {
    data: videoLinks,
    isError: errorFetchingVideoLinks,
    isFetching: areVideoLinksLoading,
  } = useGetAllVideoLinks(
    additionalProps?.houseType || houseType,
    additionalProps?.houseId || houseId,
  );

  if (!queryClientRef.current) {
    queryClientRef.current = new QueryClient();
    queryClientRef.current.setDefaultOptions({
      queries: {
        staleTime: 60000,
      },
    });
  }

  useEffect(
    () => {
      if (!profile || !profile.id || profile.id === 0) {
        return;
      }
      configCatProvider
        .isFeatureEnabled(SHOULD_USE_VIMEO_UPLOAD_FEATURE_FLAG, false, {
          identifier: profile.id.toString(),
        })
        .then(shouldUseVimeoUpload => {
          setIsVideoUploadEnabled(shouldUseVimeoUpload);
        });
    },
    [profile],
  );

  const [description, setDescription] = useState('');

  const onVideoLinkChange = useCallback(event => {
    setDescription(event.target.value);
    saveVideoLink(event.target.value);
    changeBottomButtonPayload({
      houseType,
      houseId,
      url: event.target.value,
      videoSource: 'YOUTUBE',
    });
  });

  const handleOnClose = useCallback(() => {
    updateHasSnackBar(false);
  });

  useEffect(() => {
    fetchVideoLink(
      additionalProps.houseType || houseType,
      additionalProps.houseId || houseId
    );
  }, []);

  useEffect(() => () => {
    updateHasSnackBar(false);
  }, []);

  useEffect(() => {
    if (videoId) {
      changeEnabledButton(false);
    } else {
      changeEnabledButton(true);
    }
  }, [videoId]);

  const [activeVideoTab, setactiveVideoTab] = useState('YOUTUBE');

  const getYoutubeContainer = () => (
    <Box>
      <InstructionHeader />
      <VideoTextInput
        description={description}
        onVideoLinkChange={onVideoLinkChange}
        houseType={houseType}
        houseId={houseId}
      />
      <ErrorBoundary errorComponent={null} logError={logUncaught}>
        <Snackbar
          open={hasSnackbar}
          onClose={handleOnClose}
          message={snackbarMessage}
          autoHideDuration={4000}
          color={snackbarState}
        />
      </ErrorBoundary>
    </Box>
  );

  if (areVideoLinksLoading && !videoLinks) {
    return <Skeleton width="500px" height="500px" />;
  }

  if (errorFetchingVideoLinks) {
    return (<FeedbackBlock
      kind="critical"
      title={messages.errorFetchingLinksTitle}
      subtitle={messages.errorFetchingLinksDescription}
      icon={CloseIcon}
      showButton={false}
    />);
  }

  const houseHasVimeoLinks = videoLinks?.find(
    videoLink => videoLink.videoSource === 'VIMEO',
  );
  const houseHasYoutubeLinks = videoLinks?.find(
    videoLink => videoLink.videoSource === 'YOUTUBE',
  );

  if (houseHasVimeoLinks && houseHasYoutubeLinks) {
    const lastVimeoVideo = videoLinks.find(videoLink => videoLink.videoSource === 'VIMEO');

    return (<Box>
      <Tabs
        activeTab={activeVideoTab}
        onChange={setactiveVideoTab}
      >
        <Tab id="YOUTUBE" label="Youtube" />
        <Tab id="VIMEO" label="Vimeo" />
      </Tabs>
      <Box mt={3}>
        {activeVideoTab === 'YOUTUBE' ?
          getYoutubeContainer() :
          <QueryClientProvider client={queryClientRef.current}>
            <VideoUploaderContainer
              houseId={houseId}
              vimeoLink={lastVimeoVideo?.sourceId}
              vimeoVideoId={lastVimeoVideo?.id}
              vimeoStatus={lastVimeoVideo?.status}
            />
          </QueryClientProvider>}
      </Box>
    </Box>);
  }

  if (houseHasYoutubeLinks) {
    return getYoutubeContainer();
  }

  if (houseHasVimeoLinks || isVideoUploadEnabled) {
    const lastVimeoVideo = videoLinks?.find(videoLink => videoLink.videoSource === 'VIMEO');
    return (
      <QueryClientProvider client={queryClientRef.current}>
        <VideoUploaderContainer
          houseId={houseId}
          vimeoLink={lastVimeoVideo?.sourceId || null}
          vimeoVideoId={lastVimeoVideo?.id || null}
          vimeoStatus={lastVimeoVideo?.status || null}
        />
      </QueryClientProvider>
    );
  }

  return getYoutubeContainer();
};

const mapStateToProps = createStructuredSelector({
  profile: makeSelectProfile(),
  hasSnackbar: makeSelectHasSnackbar(),
  snackbarMessage: makeSelectSnackbarMessage(),
  snackbarState: makeSelectSnackbarState(),
  videoId: makeSelectVideoId(),
});

export const mapDispatchToProps = (dispatch) => ({
  fetchVideoLink: (houseType, houseId) => dispatch(fetchVideoLinkAction(houseType, houseId)),
  saveVideoLink: (url) => dispatch(saveVideoLinkAction(url)),
  updateHasSnackBar: (state) => dispatch(updateHasSnackBarAction(state)),
  changeEnabledButton: (enabledButton) => dispatch(changeEnabledButtonAction(enabledButton)),
  changeBottomButtonPayload: (payload) => {
    const action = changeBottomButtonPayloadAction(payload);
    return dispatch(action);
  },
});

Video.propTypes = {
  additionalProps: PropTypes.shape({
    photoSessionId: PropTypes.number,
    houseId: PropTypes.number,
    houseType: PropTypes.string,
  }),
  fetchVideoLink: PropTypes.func,
  saveVideoLink: PropTypes.func,
  updateHasSnackBar: PropTypes.func,
  hasSnackbar: PropTypes.bool,
  snackbarMessage: PropTypes.string,
  snackbarState: PropTypes.string,
  videoId: PropTypes.string,
  changeEnabledButton: PropTypes.func,
  params: PropTypes.object,
  changeBottomButtonPayload: PropTypes.func,
  profile: PropTypes.object,
};

export default connect(mapStateToProps, mapDispatchToProps)(Video);
